import {
  ACCESS_TAG_COLORS,
  ACCESS_TAG_LABELS,
  ACCESS_TAG_TOOLTIPS,
  STATUS_CONFIG,
} from './constants';
import type {
  AccessTag,
  AccessTagColor,
  LicenseSummary,
  UserStatus,
  UserStatusFilter,
} from './types';

/**
 * Derives the lifecycle state shown in the Status column.
 *
 * The API persists no invite state — `invite_now` only fires a reset-password
 * email — so "invited" is inferred from an enabled account that has never been
 * used. A null `last_seen_at` means the API has recorded no activity signal for
 * the user at all, which is the closest thing we have to "never signed in".
 *
 * Keyed off `last_seen_at` rather than `last_login` (MKT-557). The display
 * `last_login` is derived from `UserActivity`, and **CS impersonation writes
 * `UserActivity` rows** — so a user who never signed in but was impersonated
 * read as Active, which also hid the Resend Invite button in `ActionButtons`
 * from precisely the person who never got in. `record_last_seen` skips
 * impersonated requests, so it cannot be inflated that way.
 *
 * Not a behaviour change for existing accounts: `backfill_profile_last_seen`
 * seeds from `Greatest(last_login, max UserActivity.date)`, the same signal set
 * that makes the display `last_login` non-null, so the two agree on null-ness
 * for every user who predates the backfill. Only new impersonations diverge.
 */
export const getUserStatus = (user?: MedScout.User | null): UserStatus => {
  if (!user?.is_active) return 'deactivated';
  return user?.last_seen_at ? 'active' : 'invited';
};

/** Accessible description of a status, e.g. for a tooltip or `sr-only` text. */
export const getStatusDescription = (status: UserStatus): string =>
  STATUS_CONFIG[status]?.description ?? '';

/**
 * Every status description in lifecycle order, for the Status column's legend.
 * Rendered a line at a time — as one paragraph it ran too wide to read.
 */
export const getStatusLegend = (): string[] =>
  (['active', 'invited', 'deactivated'] as UserStatus[]).map(
    getStatusDescription
  );

/**
 * Parses either timestamp shape the API sends for a user: `last_seen_at` is a
 * DRF `DateTimeField` and arrives as ISO-8601, while `last_login` is hand-
 * formatted `YYYY-MM-DD HH:MM:SS`, whose space separator is not valid ISO-8601.
 * Swapping in `T` fixes the latter and is a no-op on the former, rather than
 * relying on engine leniency for either.
 */
const parseTimestamp = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value.replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/** Short date for the Last Active cell — `Never` when there is no activity. */
export const formatLastActive = (value?: string | null): string => {
  const parsed = parseTimestamp(value);
  if (!parsed) return 'Never';

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Fuller date for the Last Active cell's tooltip. Deliberately a date and not a
 * `toLocaleString` timestamp: `last_seen_at` is stamped at most once a day, so a
 * to-the-second time would assert a precision the figure does not have.
 */
export const formatLastActiveDetail = (value?: string | null): string => {
  const parsed = parseTimestamp(value);
  if (!parsed) return 'No activity recorded';

  return parsed.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/** Turns a role code (`license_manager`) into a label (`License Manager`). */
export const humanizeRoleCode = (code?: string | null): string =>
  (code ?? '')
    .split(/[._-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Collapses a user's assigned roles and permission flags into the chips shown
 * in the Access column, de-duplicated by label so a `manager` role and the
 * `is_manager` flag do not both render.
 *
 * LIR eligibility is deliberately absent: it is a MedScout reporting flag, not
 * access, and it only added noise to the column. It is still editable on the
 * user detail card for MedScout staff.
 */
export const getAccessTags = (user?: MedScout.User | null): AccessTag[] => {
  const tags: AccessTag[] = [];
  const seen = new Set<string>();

  const addTag = (label: string, tooltip: string, color: AccessTagColor) => {
    const key = label.trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    tags.push({ label, tooltip, color });
  };

  (user?.roles ?? []).forEach((code) =>
    addTag(
      humanizeRoleCode(code),
      ACCESS_TAG_TOOLTIPS.role,
      ACCESS_TAG_COLORS.role
    )
  );

  if (user?.permissions?.is_manager) {
    addTag(
      ACCESS_TAG_LABELS.manager,
      ACCESS_TAG_TOOLTIPS.manager,
      ACCESS_TAG_COLORS.manager
    );
  }

  if (user?.permissions?.is_integration_admin) {
    addTag(
      ACCESS_TAG_LABELS.integrationAdmin,
      ACCESS_TAG_TOOLTIPS.integrationAdmin,
      ACCESS_TAG_COLORS.integrationAdmin
    );
  }

  return tags;
};

/**
 * Seat and headcount figures for the license overview panel, taken wholesale
 * from the company payload's user counts (MKT-445) — the API breaks the account
 * down by lifecycle state, so none of this is inferred from the loaded rows and
 * all of it stays correct on accounts too large to load in one page.
 */
export const getLicenseSummary = (
  company?: MedScout.Company | null
): LicenseSummary => {
  const totalUsers = company?.user_count ?? 0;

  // "Can sign in", which includes invited users — they hold a seat from the
  // moment the account exists. Deliberately not defaulted to 0: an unknown
  // figure here would report every contracted seat as free, which reads as an
  // account with room to spare rather than as missing data. An API build
  // predating MKT-445 has no total, and its `active_user_count` still carries
  // the old "everyone who can sign in" meaning — which is this same figure.
  const enabledUsers =
    typeof company?.total_active_user_count === 'number'
      ? company.total_active_user_count
      : (company?.active_user_count ?? 0);
  const totalLicenses = company?.max_seats ?? 0;
  const hasLicenseLimit = totalLicenses > 0;

  return {
    totalUsers,
    activeUsers: company?.active_user_count ?? 0,
    // Null, not 0 — the card renders a placeholder rather than asserting that
    // nobody is waiting to sign in (MKT-445).
    invitedUsers: company?.invited_user_count ?? null,
    enabledUsers,
    deactivatedUsers:
      company?.inactive_user_count ?? Math.max(0, totalUsers - enabledUsers),
    totalLicenses,
    usedLicenses: enabledUsers,
    availableLicenses: hasLicenseLimit
      ? Math.max(0, totalLicenses - enabledUsers)
      : 0,
    hasLicenseLimit,
    usedPercentage: hasLicenseLimit
      ? Math.min(100, Math.round((enabledUsers / totalLicenses) * 100))
      : 0,
  };
};

/** Narrows a page of users to the selected status filter. */
export const filterUsersByStatus = (
  users: MedScout.User[] = [],
  filter: UserStatusFilter
): MedScout.User[] =>
  filter === 'all'
    ? users
    : users.filter((user) => getUserStatus(user) === filter);

/**
 * Name and email search, run client-side because the whole company is already
 * loaded. Mirrors the API's `search_fields` on `CompanyUserList`
 * (username, first_name, last_name, email) — username *is* the email here.
 */
export const filterUsersBySearch = (
  users: MedScout.User[] = [],
  term = ''
): MedScout.User[] => {
  const query = term.trim().toLowerCase();
  if (!query) return users;

  return users.filter((user) => {
    const firstName = user?.first_name ?? '';
    const lastName = user?.last_name ?? '';

    return [firstName, lastName, `${firstName} ${lastName}`, user?.username]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(query));
  });
};
